<template>
  <div class="md:mx-14 mx-5">
    <div class="bg-root-1 -mt-6 z-50 absolute text-sm text-gray-40">
      Yield Management <span class="text-primary"> / </span> Yield Profile
    </div>
    <div
      @click="goBack"
      class="top-28 z-50 cursor-pointer flex items-center justify-start w-20"
    >
      <back-arrow />
      <p class="text-13px text-root-4 ml-2 font-normal">Back</p>
    </div>
    <div class="mt-4">
      <div class="grid md:grid-cols-12 grid-cols-1 gap-5 w-full">
        <div class="md:col-span-6 w-full card bg-root">
          <div class="pt-6 pl-6 pr-8">
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-x-4">
                <div>
                  <arrow-up-graph-icon size="h-16 w-16" />
                </div>
                <div>
                  <p class="text-xs font-normal text-gray-40">
                    Yield Profile Name
                  </p>
                  <p class="text-xl text-root-2 font-semibold capitalize">
                    {{ yieldProfileDetails.name || '-' }}
                  </p>
                </div>
              </div>
              <div
                class="px-3 py-1.5 border border-gray-4 text-13px cursor-pointer flex items-center justify-between gap-x-2 font-normal rounded-3xl"
                @click="editYieldProfile"
              >
                <edit-pen-icon />
                <p class="font-light text-root-4 text-xs">Edit</p>
              </div>
            </div>
            <div class="mp-4">
              <p class="text-13px font-normal text-root-2 mb-2.5">
                Yield ID:
                <span class="font-normal text-gray-40">{{
                  yieldProfileDetails.yield_offering_id || '-'
                }}</span>
              </p>
              <p
                style="word-break: normal"
                class="text-13px font-normal text-root-2 w-4/5 mb-2.5"
              >
                Description:
                <span class="font-normal text-gray-40 capitalize">{{
                  yieldProfileDetails.description || '-'
                }}</span>
              </p>
              <p class="text-13px font-normal text-root-2 mb-2.5">
                Portfolio:
                <span class="font-normal text-gray-40">{{
                  yieldProfileDetails.portfolio_id || '-'
                }}</span
                ><span class="text-primary"></span>
              </p>
              <p class="text-13px font-normal text-root-2 mb-2.5">
                APY Rate:
                <span class="font-normal text-gray-40"
                  >{{ yieldProfileDetails.apy_rate || '0' }}%</span
                >
              </p>
            </div>
          </div>
        </div>
        <div class="md:col-span-6 w-full card bg-root">
          <div class="p-2">
            <p class="pt-6 pl-6 pr-8 text-base font-bold text-root-2 pb-3.5">
              Transaction Requirement
            </p>
            <hr />
            <div class="mt-6 pl-6 pr-8">
              <p class="text-13px font-normal text-root-2 mb-3">
                Deposit Lock Days:
                <span class="font-normal text-gray-40">{{
                  yieldProfileDetails.deposit_lock_day || '-'
                }}</span>
              </p>
              <p class="text-13px font-normal text-root-2 mb-3">
                Maximum Deposit:
                <span class="font-normal text-gray-40">{{
                  yieldProfileDetails.maximum_deposit_allowed || '-'
                }}</span>
              </p>
              <p class="text-13px font-normal text-root-2 mb-3">
                Minimum Deposit:
                <span class="font-normal text-gray-40">{{
                  yieldProfileDetails.minimum_deposit_allowed || '-'
                }}</span>
              </p>
              <p class="text-13px font-normal text-root-2 mb-3">
                Yieldable After Day:
                <span class="font-normal text-gray-40">{{
                  yieldProfileDetails.yieldable_after_day || '-'
                }}</span>
              </p>
              <p class="text-13px font-normal text-root-2 mb-3">
                Withdrawal Limit:
                <span class="font-normal text-gray-40">{{
                  yieldProfileDetails.withdrawal_limit_rate || '-'
                }}</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div>
      <div class="mt-6">
        <p class="text-lg font-bold text-root-2">Customers</p>
        <div class="mt-6" v-if="isLoading">
          <spinner
            size="medium"
            :spacing="40"
            message=""
            :line-fg-color="$primaryColor"
          />
        </div>
        <div v-else class="card2 bg-root w-full p-6 mt-4">
          <customer-table
            :tableData="yieldProfileCustomersInfo"
            :page="page"
            @getYieldProfileCustomers="getYieldProfileCustomers"
            tableType="yieldProfileDetailsTable"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { GetCurrencyCountry } from '@/utils/helpers.js'
import getYieldProfileCustomers from '@/mixins/externalApi.js'
export default {
  name: 'YieldProfileDetails',
  mixins: [getYieldProfileCustomers],
  data () {
    return {
      isLoading: false,
      payload: {
        page: 1,
        size: 10,
        status: '',
        type: '',
        from: '',
        to: ''
      }
    }
  },
  components: {
    BackArrow: () => import('@/assets/icons/BackArrow.vue'),
    ArrowUpGraphIcon: () => import('@/assets/icons/ArrowUpGraphIcon.vue'),
    EditPenIcon: () => import('@/assets/icons/EditPenIcon.vue'),
    // CurrencyIcons: () => import('@/assets/icons/CurrencyIcons.vue'),
    CustomerTable: () =>
      import('@/components/externalApi/tables/CustomerTable.vue')
  },
  computed: {
    yieldProfileDetails () {
      return this.$store.state.externalApi.yieldProfileDetails || {}
    },
    yieldProfileCustomersInfo () {
      return this.$store.state.externalApi.yieldProfileCustomers.items || []
    },
    page () {
      return this.$store.state.externalApi.yieldProfileCustomers.page
    }
  },
  methods: {
    goBack () {
      this.$router.push('/api-management')
    },
    editYieldProfile () {
      this.$router
        .push('/api-management')
        .then(() =>
          this.$store.dispatch(
            'externalApi/setActiveComponent',
            'YieldProfileForm'
          )
        )
    },
    getCurrencyCountry (val) {
      return GetCurrencyCountry(val)
    },
    async getYieldProfileDetails () {
      const yieldProfileId = this.$route.params.id
      const vm = this
      await this.$store
        .dispatch('externalApi/getYieldProfileDetails', yieldProfileId)
        .then(() => {})
        .catch((error) => {
          const err = error.response
            ? error.response.data.error.publicMessage
            : error
          vm.$message({
            showClose: true,
            message: this.httpErrorMessage,
            type: 'error'
          })
        })
    }
  },
  mounted () {
    this.getYieldProfileDetails()
    this.getYieldProfileCustomers()
  }
}
</script>

<style scoped>
.card {
  height: 282px;
  box-shadow:
    0px 0px 1px rgba(3, 2, 7, 0.12),
    0px 2px 4px rgba(3, 2, 7, 0.12);
  border-radius: 15px;
}
.card2 {
  box-shadow:
    0px 0px 1px rgba(3, 2, 7, 0.12),
    0px 2px 4px rgba(3, 2, 7, 0.12);
  border-radius: 15px;
}
</style>
